<template>
  <div class="page-container expenses-page">
    <portal to="page-name">Serviços, taxas e outras despesas</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Serviços, taxas e outras despesas</h1>
        <div class="columns">
          <div class="column">
            <div class="text-dark-gray mt-1">Gerencie os serviços</div>
          </div>
          <div class="column text-right">
            <button class="btn btn-primary btn-icon btn-icon-left mr-2"
                    :disabled="deleting"
                    @click="open">
              <fa-icon :icon="['fal', 'plus']"/>Novo serviço
            </button>
            <dx-dropdown
              class="btn btn-icon btn-action btn-gray-outline drop-menu input-group-addon"
              :class="{loading: printing}"
              :disabled="printing || deleting"
              :items="menuItems"
              @select="selectedMenu"
              label="name"
              direction="right"
            >
              <fa-icon :icon="['fal', 'bars']" />
            </dx-dropdown>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-3 col-md-6 col-sm-12 form-group">
            <label class="form-label">Plano de saúde</label>
            <select class="form-select"
                    v-model="filter.insuranceKey"
                    :disabled="deleting"
                    @change="find">
              <option value="">Todos os planos</option>
              <option v-for="(item, i) in insurances"
                      :value="item.key" :key="i">{{ item.customName }}</option>
            </select>
          </div>
          <div class="column col-2 col-md-6 col-sm-12 form-group">
            <label class="form-label">Tipo do serviço</label>
            <select class="form-select"
                    v-model="filter.type"
                    :disabled="deleting"
                    @change="find">
              <option value="">Todos os tipos</option>
              <option v-for="(text, value) in expenseTypes"
                      :value="value" :key="value">{{ text }}</option>
            </select>
          </div>
          <div class="column col-3 col-md-4 col-sm-12 form-group">
            <label class="form-label">Modalidade</label>
            <select class="form-select"
                    v-model="filter.modality"
                    :disabled="deleting"
                    @change="find">
              <option value="">Todas as modalidades</option>
              <option v-for="(text, value, i) in modalities" :key="i"
                      :value="value">{{ value }} - {{ text }}</option>
            </select>
          </div>
          <div class="column form-group">
            <label class="form-label">Nome do serviço</label>
            <div class="input-group">
              <input type="text" class="form-input"
                     @keydown.enter="find"
                     :disabled="deleting"
                     v-model="filter.search"
                     placeholder="Pesquisar por nome do serviço">
              <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                      @click="find"
                      :disabled="deleting"
                      tabindex="-1">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
            </div>
          </div>
        </div>
        <div class="loading loading-lg mt-2" v-if="loading" />
        <template v-else>
          <div class="empty mt-2" v-if="data.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Serviços, taxas e outras despesas</p>
            <p class="empty-subtitle">
              Nenhum serviço encontrado. Verifique os filtros para realizar novas buscas
            </p>
          </div>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th class="hide-sm" style="width: 40px">#</th>
              <th>Nome do serviço</th>
              <th>TUSS</th>
              <th>Tipo</th>
              <th style="width: 80px"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>
                <span class="hover-link" v-if="deleting">{{ item.name }}</span>
                <router-link :to="editLink(item)" v-else>
                  {{ item.name }}
                </router-link>
              </td>
              <td>{{ item?.tuss?.code | tuss }}</td>
              <td>{{ getTypeName(item.type) }}</td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-secondary mr-1"
                        :disabled="deleting"
                        @click="edit(item)">
                  <fa-icon :icon="['fal', 'pencil']" />
                </button>
                <button class="btn btn-sm btn-action btn-icon btn-error"
                        :class="{loading: deleting && idx === i}"
                        :disabled="deleting"
                        @click="remove(item)">
                  <fa-icon :icon="['fal', 'times']" />
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="card-footer text-center" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary btn-fixed-width"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais</button>
      </div>
    </div>

    <package-modal
      v-if="packageModalShow"
      @close="packageModalShow = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { types as expenseTypes, getName as getTypeName } from 'src/data/expense-types';
import {
  UPDATE_EXPENSE,
  PRINT_EXPENSE_LIST,
  DELETE_EXPENSE,
} from '@/data/actions/modules/general';
import { modalities } from '@/data/expense-modalities';
import { mapState } from 'vuex';
import PackageModal from './modals/PackageModal.vue';

export default {
  components: { PackageModal },
  data() {
    return {
      idx: null,
      loading: false,
      loadingMore: false,
      deleting: false,
      printing: false,
      getTypeName,
      filter: {
        insuranceKey: '',
        type: '',
        modality: '',
        search: '',
      },
      insurances: [],
      expenseTypes,
      modalities,
      menuItems: [
        { key: 'print', name: 'Imprimir' },
        { key: 'package', name: 'Gerenciar pacotes' },
      ],
      packageModalShow: false,
      data: {
        items: [],
        hasMore: false,
        limit: 100,
        offset: 0,
      },
    };
  },
  mounted() {
    this.loadInsurances();
    this.load();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    canUpdateAccess() {
      return this.$can(UPDATE_EXPENSE);
    },
    canPrintAccess() {
      return this.$can(PRINT_EXPENSE_LIST);
    },
    canRemoveAccess() {
      return this.$can(DELETE_EXPENSE);
    },
  },
  methods: {
    async loadMore() {
      this.data.offset = this.data.items.length;
      await this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
        active: true,
      };

      if (this.filter.insuranceKey) {
        const [insuranceId, planId] = this.filter.insuranceKey.split('_');
        params.insuranceId = insuranceId;
        params.planId = planId;
      }

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.modality) {
        params.modality = this.filter.modality;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search.trim();
      }

      return this.$http.get('/expenses', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    loadInsurances() {
      const params = {
        limit: 0,
        active: true,
      };
      return this.$http.get('/insurance-plans', { params })
        .then(({ data }) => {
          this.insurances = data.items.map(item => ({
            key: `${item.id}_${item.plan.id}`,
            id: item.id,
            name: item.name,
            customName: item.customName,
            plan: {
              id: item.plan.id,
              name: item.plan.name,
            },
          }));
        })
        .catch(() => {});
    },
    find() {
      this.data.offset = 0;
      this.load();
    },
    edit(item) {
      if (this.deleting) {
        return;
      }
      this.$router.push(`/clinical/expenses/${item.id}/edit`);
    },
    editLink(item) {
      return `/clinical/expenses/${item.id}/edit`;
    },
    open() {
      this.$router.push('/clinical/expenses/create');
    },
    remove(item) {
      if (!this.canRemoveAccess) {
        this.$toast.error('Acesso não autorizado!');
        return;
      }

      if (this.deleting) {
        return;
      }

      this.$dialog.show('', {
        size: 'md',
        html: `<div class="text-center">
                <p class="text-center h4">Atenção!</p>
                <p>Ao confirmar a remoção,
                 <strong class="text-primary">todos os valores</strong>
                 serão excluídos.</p>
                <p>Este registro também
                 <strong class="text-primary">será desvinculado</strong>
                 de todas as agendas, planos e demais telas do sistema.</p>
                <div class="h6 text-error text-bold">
                 Essa ação é irreversível. Deseja continuar?
                </div>
                <br>
                <div>Responsável: <strong>${this.user.name}</strong></div>
                <div>
                  Data e hora: <strong>${moment().format('DD/MM/YYYY HH:mm')}</strong>
                </div>
              </div>`,
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              const idx = this.data.items.indexOf(item);
              this.idx = idx;
              close();
              this.$http
                .delete(`/expenses/${item.id}`)
                .then(() => {
                  this.data.items.splice(idx, 1);
                })
                .catch(() => {
                  this.$toast.error('Ocorreu um erro. Tente novamente!');
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    selectedMenu(item) {
      if (item.key === 'package') {
        if (!this.canUpdateAccess) {
          this.$toast.error('Acesso não autorizado!');
          return;
        }
        this.packageModalShow = true;
      } if (item.key === 'print') {
        this.print();
      }
    },
    print() {
      if (!this.canPrintAccess) {
        this.$toast.error('Acesso não autorizado!');
        return null;
      }

      this.printing = true;

      const params = {
        active: true,
      };

      if (this.filter.insuranceKey) {
        const [insuranceId, planId] = this.filter.insuranceKey.split('_');
        params.insuranceId = insuranceId;
        params.planId = planId;
      }

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.modality) {
        params.modality = this.filter.modality;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search.trim();
      }

      return this.$file
        .print('/expenses/print', params)
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao imprimir! Tente novamente mais tarde.');
        })
        .finally(() => {
          this.printing = false;
        });
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/_variables.scss';
  .expenses-page {
    .btn-fixed-width {
      font-size: $font-size-lg;
      height: 2.2rem;
      margin: $layout-spacing-lg 0;
      width: 11rem;
    }
    .drop-menu {
      line-height: 0;
      .menu {
        color: $dark-color;
        line-height: $layout-spacing-xl;
        text-align: left;
      }
    }
  }
</style>
